import { z } from 'zod';
import {
  MAX_PRODUCING_DAY,
  SKU_VOLUME__MAX,
  SPO_DEFAULT,
  SPO_DEFAULT_INTL,
  SPO_ITEMS__MAX,
  SPO_ITEMS__MIN,
  SPO_OPTION_VALUES__MAX,
  SPO_OPTION_VALUES__MIN,
  SPO_OPTIONS__MAX,
  SPO_TEXT_OPTION__MAX,
  StockType,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/constants.ts';
import { commify } from '@services/MarppleShop/shared/util/commify.ts';
import { errorMessage } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';

const spoOptionValueSchema = z.object({
  name: z.string().min(1, { message: '옵션 상세 정보를 다시 확인해 주세요.' }),
});

export const spoOptionSchema = z.object({
  name: z.string().min(1, { message: '옵션명은 필수 사항입니다.' }),
  _: z.object({
    spo_option_values: z
      .array(spoOptionValueSchema)
      .min(SPO_OPTION_VALUES__MIN, {
        message: `옵션 상세 정보는 ${commify(SPO_OPTION_VALUES__MIN)}개 이상 필요합니다.`,
      })
      .max(SPO_OPTION_VALUES__MAX, {
        message: `옵션 상세 정보는 ${commify(SPO_OPTION_VALUES__MAX)}개 이하까지 가능합니다.`,
      })
      .refine(
        (values) => {
          const names = values.map((v) => v.name);
          return new Set(names).size === names.length;
        },
        { message: '옵션값은 중복이 불가합니다.' },
      ),
  }),
});

export const spoTextOptionSchema = z.object({
  id: z.number().optional(),
  name: z.string().max(SPO_TEXT_OPTION__MAX).optional(),
});

export const groupNamesOptionNamesSchema = z.object({
  group_name: z.string(),
  option_name: z.string(),
});

export const groupNamesOptionNamesIntlSchema = groupNamesOptionNamesSchema.extend({
  group_name_en: z.string(),
  group_name_jp: z.string(),
  option_name_en: z.string(),
  option_name_jp: z.string(),
});

const spoOptionValueIntlSchema = spoOptionValueSchema.extend({
  name_en: z.string().min(1, { message: '옵션 상세 정보를 다시 확인해 주세요.' }).nullable().default(null),
  name_jp: z.string().min(1, { message: '옵션 상세 정보를 다시 확인해 주세요.' }).nullable().default(null),
});

export const spoOptionIntlSchema = spoOptionSchema.extend({
  name_en: z.string().min(1, { message: '옵션명은 필수 사항입니다.' }).nullable().default(null),
  name_jp: z.string().min(1, { message: '옵션명은 필수 사항입니다.' }).nullable().default(null),
  _: z.object({
    spo_option_values: z
      .array(spoOptionValueIntlSchema)
      .min(SPO_OPTION_VALUES__MIN, {
        message: `옵션 상세 정보는 ${commify(SPO_OPTION_VALUES__MIN)}개 이상 필요합니다.`,
      })
      .max(SPO_OPTION_VALUES__MAX, {
        message: `옵션 상세 정보는 ${commify(SPO_OPTION_VALUES__MAX)}개 이하까지 가능합니다.`,
      })
      .refine(
        (values) => {
          const names = values.map((v) => v.name);
          return new Set(names).size === names.length;
        },
        { message: '옵션값은 중복이 불가합니다.' },
      ),
  }),
});

const spoTextOptionIntlSchema = spoTextOptionSchema.extend({
  name_en: z.string(),
  name_jp: z.string(),
});

const skuBaseSchema = z.object({
  id: z.number().optional(),
  name: z.string().nullable().default(null),
  nickname: z.string().max(100, { message: errorMessage.text_length_100 }).nullable().default(null),
});

export const skuSchema = skuBaseSchema.optional();

export const skuIntlSchema = skuBaseSchema
  .extend({
    width: z
      .number({
        coerce: true,
      })
      .min(0)
      .max(SKU_VOLUME__MAX, {
        message: `체적 가로는 ${commify(SKU_VOLUME__MAX)} 이하까지 가능합니다.`,
      })
      .nullable()
      .default(null),
    height: z
      .number({
        coerce: true,
      })
      .min(0)
      .max(SKU_VOLUME__MAX, {
        message: `체적 세로는 ${commify(SKU_VOLUME__MAX)} 이하까지 가능합니다.`,
      })
      .nullable()
      .default(null),
    length: z
      .number({
        coerce: true,
      })
      .min(0)
      .max(SKU_VOLUME__MAX, {
        message: `체적 길이는 ${commify(SKU_VOLUME__MAX)} 이하까지 가능합니다.`,
      })
      .nullable()
      .default(null),
    weight: z
      .number({
        coerce: true,
      })
      .min(0)
      .max(SKU_VOLUME__MAX, {
        message: `체적 무게는 ${commify(SKU_VOLUME__MAX)} 이하까지 가능합니다.`,
      })
      .nullable()
      .default(null),
  })
  .optional();

export const spoItemStockSchema = z.object({
  id: z.number().optional(),
  /**
   * 'real_quantity'는 오로지 보여주기 위한 값.
   * DB에 업데이트가 될 때, quantity와 real_quantity 중에 더 큰 값으로 real_quantity를 업데이트함
   */
  real_quantity: z.number().int().min(0).default(0),
  quantity: z.number().int().min(0).default(0),
  /**
   * 레거시 구조에서는 'plus_minus_quantity' 필드 값으로 재고를 업데이트
   * DB에는 존재 X
   */
  plus_minus_quantity: z.number().int().default(0),
  /**
   * 'origin'은 에디터에서 'plus_minus_quantity'를 계산하기 위한 수정되기 이전 원본 quantity
   * DB에는 존재 X
   */
  origin_quantity: z.number().int().nullable().default(null),
});

const spoItemBaseSchema = z.object({
  id: z.number().optional(),
  price: z.number().int().min(0).default(0),
  sold_quantity: z.number().int().min(0).default(0),
});

export const spoItemSchema = spoItemBaseSchema.extend({
  _: z.object({
    sku: skuSchema,
    spo_item_stock: spoItemStockSchema,
  }),
  group_names_option_names: z.array(groupNamesOptionNamesSchema).nullable().default(null),
});

export const spoItemIntlSchema = spoItemBaseSchema.extend({
  _: z.object({
    sku: skuIntlSchema,
    spo_item_stock: spoItemStockSchema,
  }),
  group_names_option_names: z.array(groupNamesOptionNamesIntlSchema).nullable().default(null),
});

export const spoSchema = z
  .object({
    id: z.number().optional(),
    stock_type: z.nativeEnum(StockType).default(SPO_DEFAULT.stock_type),
    _: z.object({
      spo_options: z
        .array(spoOptionSchema)
        .max(SPO_OPTIONS__MAX, { message: `옵션은 ${commify(SPO_OPTIONS__MAX)}개까지 입력 가능합니다.` })
        .refine((options) => new Set(options.map((opt) => opt.name)).size === options.length, {
          message: '중복된 옵션명이 존재합니다.',
        })
        .default(SPO_DEFAULT._.spo_options),
      spo_items: z
        .array(spoItemSchema)
        .max(SPO_ITEMS__MAX, { message: `옵션은 최대 ${commify(SPO_ITEMS__MAX)}개 까지 생성할 수 있습니다.` })
        .transform((spo_items) => (spo_items.length === 0 ? undefined : spo_items))
        .default(SPO_DEFAULT._.spo_items),
      spo_text_options: z.array(spoTextOptionSchema).default(SPO_DEFAULT._.spo_text_options),
      spo_producing_period: z
        .object({
          spo_id: z.number().optional(),
          max_days: z
            .number()
            .int()
            .max(MAX_PRODUCING_DAY, {
              message: `최대 ${MAX_PRODUCING_DAY}일 이하로 입력해 주세요.`,
            })
            .nullable()
            .default(null),
          avg_days: z.number().int().default(0),
        })
        .optional(),
    }),
  })
  .default(SPO_DEFAULT);

export const spoIntlSchema = z
  .object({
    stock_type: z.nativeEnum(StockType).default(SPO_DEFAULT_INTL.stock_type),
    _: z.object({
      spo_options: z
        .array(spoOptionIntlSchema)
        .max(SPO_OPTIONS__MAX, { message: `옵션은 ${commify(SPO_OPTIONS__MAX)}개까지 입력 가능합니다.` })
        .refine(
          (options) => {
            const names = options.map((opt) => opt.name);
            return new Set(names).size === names.length;
          },
          { message: '중복된 옵션명이 존재합니다.' },
        )
        .default(SPO_DEFAULT_INTL._.spo_options),
      spo_items: z
        .array(spoItemIntlSchema)
        .min(SPO_ITEMS__MIN)
        .max(SPO_ITEMS__MAX, { message: `옵션은 최대 ${commify(SPO_ITEMS__MAX)}개 까지 생성할 수 있습니다.` })
        .default(SPO_DEFAULT_INTL._.spo_items),
      spo_text_options: z.array(spoTextOptionIntlSchema).default(SPO_DEFAULT_INTL._.spo_text_options),
      spo_producing_period: z
        .object({
          spo_id: z.number().optional(),
          max_days: z
            .number()
            .int()
            .max(MAX_PRODUCING_DAY, {
              message: `최대 ${MAX_PRODUCING_DAY}일 이하로 입력해 주세요.`,
            })
            .nullable()
            .default(null),
          avg_days: z.number().int().default(0),
        })
        .optional(),
    }),
  })
  .default(SPO_DEFAULT_INTL);
